import React, { useMemo, useState } from 'react';
import {
  Dialog, Box, Typography, IconButton, Button, TextField, Tabs, Tab, Select, MenuItem, Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Close, Groups, EditNote, UploadFile, FolderOpen, Download, TaskAlt, ErrorOutline, Link as LinkIcon, InfoOutlined } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { updateGvLine, replaceGvLines, setGvDelivery, newLineKey } from '../../../store/giftVoucherSaleSlice';
import { useToast } from '../../common/ToastProvider';
import { GV, EMAIL_RE, inr, normPhone, typeAllowsValue } from './gvUtils';

// Minimal RFC-4180-ish CSV reader (quoted fields, commas, CRLF) — enough for HR lists.
function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = '';
  let quoted = false;
  const src = text.replace(/^\uFEFF/, '');
  for (let i = 0; i < src.length; i += 1) {
    const c = src[i];
    if (quoted) {
      if (c === '"' && src[i + 1] === '"') { field += '"'; i += 1; } else if (c === '"') quoted = false; else field += c;
    } else if (c === '"') quoted = true;
    else if (c === ',') { row.push(field); field = ''; } else if (c === '\n' || c === '\r') {
      if (c === '\r' && src[i + 1] === '\n') i += 1;
      row.push(field); rows.push(row); row = []; field = '';
    } else field += c;
  }
  if (field !== '' || row.length) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}

// Same checks as the API's recipients_csv (row = spreadsheet line; header = 1).
function readRecipients(text, type) {
  const rows = parseCsv(text);
  if (rows.length < 2) return { errors: [{ row: '—', error: 'The file has no rows' }] };
  const head = rows[0].map((h) => h.trim().toLowerCase());
  const ix = (k) => head.indexOf(k);
  if (ix('email') < 0 || ix('amount') < 0) return { errors: [{ row: 1, error: 'Header must include email and amount (name and phone are optional)' }] };
  if (rows.length - 1 > 1000) return { errors: [{ row: '—', error: 'At most 1000 people per upload' }] };
  const errors = [];
  const people = [];
  rows.slice(1).forEach((r, i) => {
    const row = i + 2;
    const get = (k) => (ix(k) >= 0 ? (r[ix(k)] || '').trim() : '');
    const email = get('email'); const phone = get('phone'); const amount = get('amount');
    if (!EMAIL_RE.test(email)) { errors.push({ row, error: 'email is missing or invalid' }); return; }
    if (phone && normPhone(phone).length !== 10) { errors.push({ row, error: 'phone must be 10 digits' }); return; }
    if (!/^\d+(\.\d{1,2})?$/.test(amount) || Number(amount) <= 0) { errors.push({ row, error: 'amount must be a positive number with at most 2 decimals' }); return; }
    if (!typeAllowsValue(type, amount)) { errors.push({ row, error: `${Number(amount).toFixed(1)} is not an allowed value for ${type.name}` }); return; }
    people.push({ name: get('name'), email, phone, amount: Number(amount) });
  });
  if (errors.length) return { errors };
  // One line per distinct amount, in first-seen order (matches the API).
  const byAmount = new Map();
  people.forEach((p) => { if (!byAmount.has(p.amount)) byAmount.set(p.amount, []); byAmount.get(p.amount).push(p); });
  const lines = [...byAmount.entries()].map(([v, list]) => ({ value: v, people: list }));
  return { lines, count: people.length, total: people.reduce((a, p) => a + p.amount, 0) };
}

const TEMPLATE = 'name,email,phone,amount\nAsha Iyer,asha@company.com,9880000202,1000\n';

const cellInputSx = { '& .MuiOutlinedInput-root': { height: 34, borderRadius: '8px', fontSize: '0.78rem', bgcolor: '#FFF' }, minWidth: 110 };

// Content lives in its own component so it mounts fresh every time the
// dialog opens (MUI unmounts closed dialog content) — no reset effect needed.
function RecipientsContent({ lineKey, startTab, types, onClose, isMobile }) {
  const dispatch = useDispatch();
  const showToast = useToast();
  const sale = useSelector((s) => s.giftVoucherSale);
  const line = sale.lines.find((l) => l.key === lineKey);
  const lineIndex = sale.lines.findIndex((l) => l.key === lineKey);
  const type = types.find((t) => t.id === line?.typeId);

  const [tab, setTab] = useState(line ? startTab : 'csv');
  const [rows, setRows] = useState(() => (line
    ? Array.from({ length: Number(line.qty) }, (_, i) => ({ name: '', email: '', phone: '', ...(line.recipients[i] || {}) }))
    : []));
  const [errors, setErrors] = useState({});
  const [csvTypeId, setCsvTypeId] = useState(line?.typeId || types[0]?.id || '');
  const [csvResult, setCsvResult] = useState(null);
  const [fileName, setFileName] = useState('');
  const [dragOver, setDragOver] = useState(false);

  // Customer links come back from the API once the draft is saved.
  const linked = useMemo(() => {
    const serverLine = sale.server?.lines?.[lineIndex];
    const map = {};
    (serverLine?.recipients || []).forEach((r) => { if (r.customer_id && r.email) map[r.email.toLowerCase()] = r.customer_id; });
    return map;
  }, [sale.server, lineIndex]);

  const setRow = (i, k, v) => {
    setRows((prev) => prev.map((r, ix) => (ix === i ? { ...r, [k]: v } : r)));
    setErrors((prev) => ({ ...prev, [`${i}${k[0]}`]: undefined }));
  };

  const savePeople = () => {
    const e = {};
    rows.forEach((r, i) => {
      if (!r.name.trim()) e[`${i}n`] = 'Name is required';
      if (!EMAIL_RE.test(r.email.trim())) e[`${i}e`] = r.email.trim() ? 'Email is invalid' : 'Email is required';
      if (r.phone && normPhone(r.phone).length !== 10) e[`${i}p`] = 'Phone must be 10 digits';
    });
    setErrors(e);
    const first = Object.keys(e)[0];
    if (first) { showToast(`Recipient ${parseInt(first, 10) + 1}: ${e[first]}`, 'error'); return; }
    dispatch(updateGvLine({ key: line.key, changes: { recipients: rows.map((r) => ({ name: r.name.trim(), email: r.email.trim(), phone: r.phone.trim() })) } }));
    showToast('People saved', 'success');
    onClose();
  };

  const readFile = (file) => {
    if (!file) return;
    setFileName(file.name);
    if (!/\.csv$/i.test(file.name)) { setCsvResult({ errors: [{ row: '—', error: 'Upload a .csv file' }] }); return; }
    if (file.size > 2 * 1024 * 1024) { setCsvResult({ errors: [{ row: '—', error: 'File is too large (max 2 MB)' }] }); return; }
    const t = types.find((x) => x.id === csvTypeId);
    const reader = new FileReader();
    reader.onload = () => setCsvResult(readRecipients(String(reader.result), t));
    reader.onerror = () => setCsvResult({ errors: [{ row: '—', error: 'Could not read the file' }] });
    reader.readAsText(file);
  };

  const applyCsvLines = () => {
    dispatch(replaceGvLines(csvResult.lines.map((l) => ({
      key: newLineKey(), typeId: csvTypeId, value: l.value, qty: l.people.length,
      recipients: l.people.map((p) => ({ name: p.name, email: p.email, phone: p.phone })),
    }))));
    dispatch(setGvDelivery('individual'));
    showToast(`Lines rebuilt from file · ${inr(csvResult.total)}`, 'success');
    onClose();
  };

  const downloadTemplate = () => {
    const url = URL.createObjectURL(new Blob([TEMPLATE], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'gift-voucher-recipients.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const filledCount = rows.filter((r) => r.name.trim() && r.email.trim()).length;
  const title = tab === 'csv' || !line ? 'Upload recipient list' : `People for ${type?.name || 'voucher'} · ${inr(line.value)} × ${line.qty}`;

  return (
    <>
      <Box sx={{ px: 2.5, pt: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: GV.bg, color: GV.main, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Groups sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#1E293B', lineHeight: 1.25 }}>{title}</Typography>
          <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8' }}>
            {tab === 'csv' ? 'One row per person. Lines are rebuilt from the file.' : 'One person per voucher. Phones that match a customer are linked.'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: '#94A3B8' }} aria-label="Close"><Close sx={{ fontSize: 18 }} /></IconButton>
      </Box>

      {line && (
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ px: 2.5, minHeight: 40, borderBottom: '1px solid #E2E8F0', '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontSize: '0.78rem', fontWeight: 700, px: 1.5 }, '& .Mui-selected': { color: '#4F46E5 !important' }, '& .MuiTabs-indicator': { bgcolor: '#4F46E5' } }}>
          <Tab value="list" icon={<EditNote sx={{ fontSize: 16 }} />} iconPosition="start" label="Type the list" />
          <Tab value="csv" icon={<UploadFile sx={{ fontSize: 16 }} />} iconPosition="start" label="Upload CSV" />
        </Tabs>
      )}

      <Box sx={{ px: 2.5, py: 2, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {tab === 'list' && line ? (
          <>
            {isMobile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {rows.map((r, i) => (
                  <Box key={i} sx={{ border: '1px solid #E2E8F0', borderRadius: '10px', p: 1.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#64748B' }}>Person {i + 1}</Typography>
                      {linked[r.email.toLowerCase()] && <Chip size="small" icon={<LinkIcon sx={{ fontSize: '12px !important' }} />} label={`#${linked[r.email.toLowerCase()]}`} sx={{ height: 20, fontSize: '0.6rem', fontWeight: 800, bgcolor: '#F0FDF4', color: '#16A34A' }} />}
                    </Box>
                    <TextField size="small" placeholder="Name *" value={r.name} onChange={(e) => setRow(i, 'name', e.target.value)} error={!!errors[`${i}n`]} helperText={errors[`${i}n`]} sx={cellInputSx} inputProps={{ 'aria-label': `Name ${i + 1}` }} />
                    <TextField size="small" placeholder="Email *" type="email" value={r.email} onChange={(e) => setRow(i, 'email', e.target.value)} error={!!errors[`${i}e`]} helperText={errors[`${i}e`]} sx={cellInputSx} inputProps={{ 'aria-label': `Email ${i + 1}` }} />
                    <TextField size="small" placeholder="Phone (optional)" value={r.phone} onChange={(e) => setRow(i, 'phone', e.target.value)} error={!!errors[`${i}p`]} helperText={errors[`${i}p`]} sx={cellInputSx} inputProps={{ inputMode: 'tel', 'aria-label': `Phone ${i + 1}` }} />
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ overflowX: 'auto', border: '1px solid #E2E8F0', borderRadius: '10px' }}>
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                  <Box component="thead"><Box component="tr">
                    {['#', 'NAME *', 'EMAIL *', 'PHONE', ''].map((h) => (
                      <Box component="th" key={h || 'l'} sx={{ bgcolor: '#F8FAFC', fontSize: '0.62rem', fontWeight: 800, color: '#64748B', textAlign: 'left', px: 1.25, py: 1, whiteSpace: 'nowrap' }}>{h}</Box>
                    ))}
                  </Box></Box>
                  <Box component="tbody">
                    {rows.map((r, i) => (
                      <Box component="tr" key={i}>
                        <Box component="td" sx={{ color: '#94A3B8', fontWeight: 700, px: 1.25, pt: 1.75, verticalAlign: 'top', width: 28, borderTop: '1px solid #F1F5F9' }}>{i + 1}</Box>
                        {[['name', 'n'], ['email', 'e'], ['phone', 'p']].map(([k, e]) => (
                          <Box component="td" key={k} sx={{ px: 1, py: 0.75, verticalAlign: 'top', borderTop: '1px solid #F1F5F9' }}>
                            <TextField fullWidth size="small" value={r[k]} onChange={(ev) => setRow(i, k, ev.target.value)} error={!!errors[`${i}${e}`]} helperText={errors[`${i}${e}`]}
                              type={k === 'email' ? 'email' : 'text'} sx={cellInputSx} inputProps={{ 'aria-label': `${k} ${i + 1}`, ...(k === 'phone' ? { inputMode: 'tel' } : {}) }}
                              FormHelperTextProps={{ sx: { fontSize: '0.64rem', mx: 0 } }} />
                          </Box>
                        ))}
                        <Box component="td" sx={{ px: 1, pt: 1.5, verticalAlign: 'top', whiteSpace: 'nowrap', borderTop: '1px solid #F1F5F9' }}>
                          {linked[r.email.toLowerCase()] && (
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, fontSize: '0.64rem', fontWeight: 700, color: '#16A34A' }}>
                              <LinkIcon sx={{ fontSize: 13 }} />Customer #{linked[r.email.toLowerCase()]}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            )}
            <Typography sx={{ fontSize: '0.66rem', color: '#94A3B8', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <InfoOutlined sx={{ fontSize: 13 }} /> Phone is optional: 10 digits, +91 / 0 prefixes and spaces are fine.
            </Typography>
          </>
        ) : (
          <>
            {csvResult?.lines ? (
              <>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', px: 1.5, py: 1.1, borderRadius: '8px', bgcolor: '#F0FDF4', border: '1px solid #BBF7D0', color: '#166534', fontSize: '0.75rem' }}>
                  <TaskAlt sx={{ fontSize: 18 }} />
                  <Box><b>{csvResult.count} people read from {fileName}</b><Box sx={{ fontSize: '0.68rem', mt: 0.25 }}>Lines will be rebuilt for {types.find((t) => t.id === csvTypeId)?.name}. This replaces the current lines.</Box></Box>
                </Box>
                <Box sx={{ overflowX: 'auto', border: '1px solid #E2E8F0', borderRadius: '10px' }}>
                  <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                    <Box component="thead"><Box component="tr">
                      {['LINE', 'VALUE', 'QTY', 'PEOPLE', 'TOTAL'].map((h) => <Box component="th" key={h} sx={{ bgcolor: '#F8FAFC', fontSize: '0.62rem', fontWeight: 800, color: '#64748B', textAlign: h === 'TOTAL' ? 'right' : 'left', px: 1.25, py: 1 }}>{h}</Box>)}
                    </Box></Box>
                    <Box component="tbody">
                      {csvResult.lines.map((l, i) => (
                        <Box component="tr" key={l.value}>
                          <Box component="td" sx={{ px: 1.25, py: 1, borderTop: '1px solid #F1F5F9', color: '#94A3B8', fontWeight: 700 }}>{i + 1}</Box>
                          <Box component="td" sx={{ px: 1.25, py: 1, borderTop: '1px solid #F1F5F9', fontWeight: 800 }}>{inr(l.value)}</Box>
                          <Box component="td" sx={{ px: 1.25, py: 1, borderTop: '1px solid #F1F5F9' }}>{l.people.length}</Box>
                          <Box component="td" sx={{ px: 1.25, py: 1, borderTop: '1px solid #F1F5F9', color: '#64748B' }}>{l.people.slice(0, 6).map((p) => p.name || p.email).join(', ')}{l.people.length > 6 ? ` +${l.people.length - 6}` : ''}</Box>
                          <Box component="td" sx={{ px: 1.25, py: 1, borderTop: '1px solid #F1F5F9', textAlign: 'right', fontWeight: 800 }}>{inr(l.value * l.people.length)}</Box>
                        </Box>
                      ))}
                      <Box component="tr">
                        <Box component="td" colSpan={4} sx={{ px: 1.25, py: 1, borderTop: '1px solid #F1F5F9', fontWeight: 900 }}>Payable before discount</Box>
                        <Box component="td" sx={{ px: 1.25, py: 1, borderTop: '1px solid #F1F5F9', textAlign: 'right', fontWeight: 900, color: '#4F46E5' }}>{inr(csvResult.total)}</Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </>
            ) : (
              <>
                {csvResult?.errors && (
                  <>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', px: 1.5, py: 1.1, borderRadius: '8px', bgcolor: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', fontSize: '0.75rem' }}>
                      <ErrorOutline sx={{ fontSize: 18 }} />
                      <Box><b>Some rows are invalid — fix them and upload again.</b><Box sx={{ fontSize: '0.68rem', mt: 0.25 }}>Nothing was saved. Row numbers match your spreadsheet (header is row 1).</Box></Box>
                    </Box>
                    <Box sx={{ overflowX: 'auto', border: '1px solid #E2E8F0', borderRadius: '10px', maxHeight: 220 }}>
                      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                        <Box component="thead"><Box component="tr">
                          {['ROW', 'PROBLEM'].map((h) => <Box component="th" key={h} sx={{ bgcolor: '#F8FAFC', fontSize: '0.62rem', fontWeight: 800, color: '#64748B', textAlign: 'left', px: 1.25, py: 1, position: 'sticky', top: 0 }}>{h}</Box>)}
                        </Box></Box>
                        <Box component="tbody">
                          {csvResult.errors.map((e, i) => (
                            <Box component="tr" key={i}>
                              <Box component="td" sx={{ px: 1.25, py: 0.9, borderTop: '1px solid #F1F5F9', fontWeight: 800, width: 60 }}>{e.row}</Box>
                              <Box component="td" sx={{ px: 1.25, py: 0.9, borderTop: '1px solid #F1F5F9', color: '#DC2626' }}>{e.error}</Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  </>
                )}
                <Box>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748B', mb: 0.5 }}>Voucher type for every row</Typography>
                  <Select size="small" value={csvTypeId} onChange={(e) => { setCsvTypeId(e.target.value); setCsvResult(null); }} sx={{ minWidth: 220, height: 36, borderRadius: '8px', fontSize: '0.82rem' }} inputProps={{ 'aria-label': 'Voucher type for the file' }}>
                    {types.map((t) => <MenuItem key={t.id} value={t.id} sx={{ fontSize: '0.82rem' }}>{t.name} · {t.denominations.map((d) => inr(d)).join(', ')}</MenuItem>)}
                  </Select>
                </Box>
                <Box
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => { e.preventDefault(); setDragOver(false); readFile(e.dataTransfer.files?.[0]); }}
                  sx={{ border: '2px dashed', borderColor: dragOver ? '#4F46E5' : '#CBD5E1', bgcolor: dragOver ? '#EEF2FF' : '#F8FAFC', borderRadius: '12px', py: 2.75, px: 1.75, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  <UploadFile sx={{ fontSize: 34, color: '#6366F1' }} />
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>Drop the CSV here, or choose a file</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: '#64748B' }}>
                    Columns <Box component="span" sx={{ fontFamily: 'monospace' }}>name, email, phone, amount</Box> · header row required · up to 1,000 people · 2 MB
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Button component="label" variant="contained" disableElevation startIcon={<FolderOpen />}
                      sx={{ bgcolor: '#4F46E5', borderRadius: '8px', textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: '#4338CA' } }}>
                      Choose file
                      <input hidden type="file" accept=".csv,text/csv" onChange={(e) => { readFile(e.target.files?.[0]); e.target.value = ''; }} />
                    </Button>
                    <Button onClick={downloadTemplate} startIcon={<Download />}
                      sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, border: '1px solid #E2E8F0', color: '#64748B', bgcolor: '#FFF' }}>
                      Template
                    </Button>
                  </Box>
                </Box>
              </>
            )}
          </>
        )}
      </Box>

      <Box sx={{ px: 2.5, py: 1.5, display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-end', borderTop: '1px solid #F1F5F9', flexWrap: 'wrap', '& .MuiButton-root': { minHeight: 38, ...(isMobile ? { flex: 1 } : {}) } }}>
        {tab === 'list' && line && <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', mr: 'auto' }}>{filledCount} of {rows.length} people</Typography>}
        <Button onClick={onClose} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, border: '1px solid #E2E8F0', color: '#64748B' }}>Cancel</Button>
        {tab === 'list' && line && (
          <Button variant="contained" disableElevation onClick={savePeople} sx={{ bgcolor: '#4F46E5', borderRadius: '8px', textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: '#4338CA' } }}>Save people</Button>
        )}
        {tab === 'csv' && csvResult?.lines && (
          <Button variant="contained" disableElevation onClick={applyCsvLines} sx={{ bgcolor: '#4F46E5', borderRadius: '8px', textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: '#4338CA' } }}>Use these lines</Button>
        )}
      </Box>
    </>
  );
}

export default function RecipientsDialog({ open, lineKey, startTab = 'list', types, onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : '12px', m: { xs: 0, sm: 3 } } }}>
      {open && <RecipientsContent lineKey={lineKey} startTab={startTab} types={types} onClose={onClose} isMobile={isMobile} />}
    </Dialog>
  );
}
